/**
 * CommandPalette - ODV Brain Natural Language Interface
 *
 * Cmd+K to open, type anything, AI handles it.
 * Uses Supabase Edge Function: odv-brain-v3
 */

import { useState, useEffect, useRef } from 'react';
import { IconCommand, IconLoader2, IconCheck, IconX, IconSparkles } from '@tabler/icons-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

const VEGAS_GOLD = '#AEA36D';

export default function CommandPalette({ isOpen, onClose }) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const { user } = useAuth();

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setInput('');
      setResult(null);
      setError(null);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('odv-brain-v3', {
        body: {
          text: input.trim(),
          user_id: user?.id,
          user_name: user?.email?.split('@')[0] || 'unknown',
        },
      });

      if (fnError) throw fnError;

      setResult(data);
    } catch (err) {
      console.error('ODV Brain error:', err);
      setError(err.message || 'Failed to process request');
    } finally {
      setIsLoading(false);
    }
  };

  const renderResult = () => {
    if (!result) return null;

    const { results, message } = result;

    return (
      <div className="p-4 space-y-3">
        {/* Tool executions */}
        {results?.map((r, i) => (
          <div key={i} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <div className="flex items-center gap-2 mb-2">
              {r.result?.success !== false ? (
                <IconCheck className="w-4 h-4 text-green-400" />
              ) : (
                <IconX className="w-4 h-4 text-red-400" />
              )}
              <span className="text-sm font-medium text-gray-300">
                {r.tool?.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="text-sm text-gray-400">
              {r.result?.error && (
                <span className="text-red-400">{r.result.error}</span>
              )}
              {r.result?.title && (
                <span>Created: {r.result.title}</span>
              )}
              {r.result?.location && r.result?.valet && (
                <span>
                  Assigned {r.result.valet} to {r.result.location}
                  {r.result.date && ` on ${r.result.date}`}
                </span>
              )}
              {r.result?.valet && r.result?.item && (
                <span>
                  {r.result.action}: {r.result.item} → {r.result.valet}
                </span>
              )}
              {r.result?.id && !r.result?.title && !r.result?.location && (
                <span className="text-gray-500">ID: {r.result.id}</span>
              )}
            </div>
          </div>
        ))}

        {/* AI message */}
        {message && (
          <div className="text-sm text-gray-300 bg-gray-800/50 rounded-lg p-3">
            {message}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/80 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
        {/* Input area */}
        <form onSubmit={handleSubmit}>
          <div className="flex items-center gap-3 p-4 border-b border-gray-800">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-800">
              {isLoading ? (
                <IconLoader2 className="w-5 h-5 animate-spin" style={{ color: VEGAS_GOLD }} />
              ) : (
                <IconSparkles className="w-5 h-5" style={{ color: VEGAS_GOLD }} />
              )}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="What do you need? (e.g., 'assign me to Na'Brasa friday')"
              className="flex-1 bg-transparent text-white text-lg outline-none placeholder-gray-500"
              disabled={isLoading}
            />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <kbd className="px-2 py-1 bg-gray-800 rounded">↵</kbd>
              <span>to send</span>
            </div>
          </div>
        </form>

        {/* Results area */}
        {(result || error) && (
          <div className="max-h-80 overflow-y-auto">
            {error ? (
              <div className="p-4 text-red-400 bg-red-900/20">
                <div className="flex items-center gap-2">
                  <IconX className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              </div>
            ) : (
              renderResult()
            )}
          </div>
        )}

        {/* Examples when empty */}
        {!result && !error && !isLoading && (
          <div className="p-4 space-y-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Try saying</p>
            {[
              'give me nabrasa friday',
              'I gave Kamar a large jacket',
              'we need more cones at Hilton',
              'pro tip: always confirm with the host',
            ].map((example, i) => (
              <button
                key={i}
                onClick={() => setInput(example)}
                className="block w-full text-left px-3 py-2 rounded-lg text-sm text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
              >
                "{example}"
              </button>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-800 text-xs text-gray-500">
          <span>ODV Brain v3</span>
          <div className="flex items-center gap-4">
            <span>
              <kbd className="px-1.5 py-0.5 bg-gray-800 rounded">esc</kbd> to close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
